import { AxiosResponse, AxiosError } from "axios";

import { ALLOWANCE_API_ENDPOINTS } from "@/features/allowance/api/endpointsAllowance";
import {
  newAllowanceModelToLegacyModel,
  legacyAllowanceItemToNewItem,
} from "@/features/allowance/helpers/formatLegacyAllowanceModels";
import { AllowanceLegacyModel } from "@/features/allowance/types/allowanceLegacyModel";
import { AllowanceBaseModel } from "@/features/allowance/types/allowanceModels";
import apiRequests from "@/shared/config/axiosConfig";

/**
 * Updates an allowance by its ID.
 * @param allowance The allowance to update.
 * @returns A Promise that resolves to the updated AllowanceBaseModel object.
 * @throws An error if the request fails or returns an error status code.
 */
export default async function editAllowance(
  allowance: AllowanceBaseModel
): Promise<AllowanceBaseModel> {
  // Convert to legacy model
  const legacyAllowanceModel = newAllowanceModelToLegacyModel(allowance);

  try {
    if (!legacyAllowanceModel.id) {
      throw new Error("Allowance ID is undefined");
    }

    const url = ALLOWANCE_API_ENDPOINTS.UPDATE_ALLOWANCE.replace(
      "{allowanceId}",
      legacyAllowanceModel.id
    );
    const response: AxiosResponse<AllowanceLegacyModel> = await apiRequests.put(
      url,
      legacyAllowanceModel
    );

    if (response.status >= 200 && response.status < 300) {
      // Convert the returned legacy model to the new model
      return legacyAllowanceItemToNewItem(response.data);
    }

    throw new Error(`Unknown error occurred with status code ${response.status}`);
  } catch (e) {
    const error = e as AxiosError;
    switch (error.response?.status) {
      case 400:
        throw new Error("400: Bad Request");
      case 401:
        throw new Error("401: Unauthorized");
      case 404:
        throw new Error("404: Not Found");
      default:
        throw new Error(
          `Unknown error occurred with status code ${error.response?.status}`
        );
    }
  }
}
